import type { FunctionViewModel, RiskAssessmentViewModel } from '@trinnvis/dabih-api-client';
import { BASE_PATH, EmployeeViewModelStatusEnum } from '@trinnvis/dabih-api-client';
import {
  calculateDataItems,
  commonFunctionUuid,
  defaultHelpForSubstancesRiskAssessment,
  employeesAndContactsOptions,
  employeesNotDeleted,
  employeesShortNamesAndI,
  employeesShortNamesAndMe,
  eventDoneText,
  eventOccurrencesNotDeleted,
  functionsNotDeleted,
  getContactsAsUsers,
  getOrganization,
  guidelinesNotDeleted,
  key,
  riskAssessmentsNotDeleted,
  singleUserVersion,
  type SuggestedRiskAssessment,
  suggestedRiskAssessments,
  tasksNotDeleted,
  toRiskAssessmentViewModel,
  writeAccess,
} from 'src/store/selectors';
import type { EmployeeViewModelWithName, State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { commonFieldsByCode } from 'src/store/fields.js';
import { shortenPath } from 'src/utilities/path';
import type { ApiClient } from 'src/store/backend';
import { calculateUpdateStatus } from 'src/models/resolvers/calculate-update-status';
import { riskLevelFromRisk } from 'src/models/risk';
import { orderBy } from 'lodash';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { ListItemIcon } from '../../library/lists/listItemIcon';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { RiskAssessmentListItem } from '../../content/risk-assessments/risk-assessment-list-item';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { RiskAssessmentView } from '../../content/risk-assessments/risk-assessment-view';
import type { RiskAssessmentViewEditItem } from '../../content/risk-assessments/risk-assessment-view-edit-item';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchRevisions } from '../fetch-revisions';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { pageIds } from '../../store/selectors/pages/pageIds';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { getUsersForAccess } from '../get-users-for-access';
import { eventToListItem, availableEventsAsListItems } from 'src/store/selectors/events/available-events-as-list-items';
import { tasksAsListItems, taskToListItem } from 'src/store/selectors/events/tasks-as-list-items';
import { functionIds } from 'src/store/selectors/functions/function-ids';
import { emergencyCauseCategories } from 'src/store/data/emergency-cause-categories';
import { isEmptyHtmlContent, joinWithAnd } from 'src/utilities/text';
import suggestedDocumentsList from 'src/store/data/suggested-documents.json';
import { functionsByRank, functionUuidsByRank } from 'src/store/selectors/functions/functions-by-rank';
import { sortByArrayAndField } from 'src/store/selectors/functions/sort-by-array-and-field';
import { t } from 'src/i18n';
import type { TaskSummary } from 'src/content/risk-assessments/task-summary';
import { eventAssigneesText } from 'src/store/eventAssigneesText';
import { dateTimeDescription } from 'src/store/utilities';
import { docsForLinkingById } from 'src/store/selectors/linking/docsForLinkingById';
import { convertInternalLinks } from 'src/models/internal-linking';
import { getPartnersAndPeopleWithContactInfo } from 'src/store/selectors/emergency/emergency-contacts';

function informationSecurityDescription(entity: RiskAssessmentViewModel) {
  if (!entity.violatesInformationSecurityRequirements) {
    return t('riskAssessments.none');
  }

  const v: string[] = [];

  if (entity.violatesConfidentialityRequirement) {
    v.push(commonFieldsByCode()['riskAssessments_violatesConfidentialityRequirement'].label);
  }
  if (entity.violatesIntegrityRequirement) {
    v.push(commonFieldsByCode()['riskAssessments_violatesIntegrityRequirement'].label);
  }
  if (entity.violatesAvailabilityRequirement) {
    v.push(commonFieldsByCode()['riskAssessments_violatesAvailabilityRequirement'].label);
  }
  if (entity.violatesNonRepudiationRequirement) {
    v.push(commonFieldsByCode()['riskAssessments_violatesNonRepudiationRequirement'].label);
  }

  if (v.length === 0) {
    return t('riskAssessments.none');
  }

  return v.join(', ');
}

function isEqual(a: string | undefined | null, b: string | undefined | null): boolean {
  if ((a === undefined || a === null) && (b === undefined || b === null)) {
    return true;
  }
  return a === b;
}

function getSameNameAssessments(
  riskAssessments: RiskAssessmentViewModel[],
  entity: RiskAssessmentViewModel,
): RiskAssessmentViewModel[] {
  const sameNameAssessments = riskAssessments.filter(
    (r) => !r.unchangedTemplate && r.name === entity.name && isEqual(r.relatedSubstance, entity.relatedSubstance),
  );
  return orderBy(sameNameAssessments, [(i) => i.assessmentDate, (i) => i.createdDateTime], ['desc', 'desc']);
}

function getPreviousAssessmentsAsListItems(
  riskAssessments: RiskAssessmentViewModel[],
  entity: RiskAssessmentViewModel,
  currentParent: string,
): ListSectionItemInput[] {
  if (getIsNewest(riskAssessments, entity)) {
    return getSameNameAssessments(riskAssessments, entity)
      .filter((r) => {
        return r.uuid !== entity.uuid;
      })
      .map((r) => {
        let href = currentParent + '/riskAssessments/' + entity.uuid + '/riskAssessments/' + r.uuid;
        if (!r.isConfirmedEntity || !r.name || r.unchangedTemplate) {
          href += '?edit';
        }
        let mainIcon: ListItemIcon | undefined;
        if (r.mainTemplateId !== undefined && r.unchangedTemplate) {
          mainIcon = 'suggestion';
        } else if (r.consequence && r.probability) {
          mainIcon = ('risk' + riskLevelFromRisk(r.consequence * r.probability)) as ListItemIcon;
        } else {
          mainIcon = 'risk0';
        }
        return {
          locked: false,
          accessible: true,
          updateStatus: calculateUpdateStatus(r, LocalDate.now()),
          hasDraft: r.hasDraft,
          label: LocalDate.fromString(r.assessmentDate).toStringForDisplay(),
          secondaryLabel: '',
          href,
          mainIcon,
        };
      });
  }
  return [];
}

function getIsNewest(riskAssessments: RiskAssessmentViewModel[], entity: RiskAssessmentViewModel): boolean {
  const sameNameAssessments = getSameNameAssessments(riskAssessments, entity);
  if (sameNameAssessments.length) {
    return sameNameAssessments[0].uuid === entity.uuid;
  }
  return true;
}

function getProbabilityDisplayText(value: number): string {
  if (value === 1) {
    return t('riskAssessments.probabilityLow');
  } else if (value === 2) {
    return t('riskAssessments.probabilityModerate');
  } else if (value === 3) {
    return t('riskAssessments.probabilityHigh');
  } else if (value === 4) {
    return t('riskAssessments.probabilityVeryHigh');
  }
  return t('riskAssessments.notAssessed');
}

function getConsequenceDisplayText(value: number): string {
  if (value === 1) {
    return t('riskAssessments.consequenceNegligible');
  } else if (value === 2) {
    return t('riskAssessments.consequenceModerate');
  } else if (value === 3) {
    return t('riskAssessments.consequenceSerious');
  } else if (value === 4) {
    return t('riskAssessments.consequenceCatastrophic');
  }
  return t('riskAssessments.notAssessed');
}

function getCalculatedRiskDisplayText(value: number): string {
  if (value > 12) {
    return t('riskAssessments.calculatedVeryHigh');
  } else if (value > 8) {
    return t('riskAssessments.calculatedHigh');
  } else if (value > 4) {
    return t('riskAssessments.calculatedModerate');
  } else if (value > 0) {
    return t('riskAssessments.calculatedLow');
  }
  return t('riskAssessments.notAssessed');
}

function getEmployeesOptions(employees: EmployeeViewModelWithName[]): SelectDropdownOption[] {
  const options = employees
    .filter((e) => e.status !== EmployeeViewModelStatusEnum.Terminated && e.isConfirmedEntity)
    .map((p) => ({ value: p.uuid, text: p.name }));
  options.push({ value: 'NEW', text: t('riskAssessments.newPerson') });
  return options;
}

function getTemplate(templateId: number): SuggestedRiskAssessment | undefined {
  return suggestedRiskAssessments().find((r) => {
    return r.pageId === templateId;
  });
}

function getSuggestedMeasures(
  templateId: number | undefined,
  list: string,
  functions: FunctionViewModel[],
): RiskAssessmentListItem[] {
  if (templateId) {
    const template = getTemplate(templateId);
    if (template) {
      // Sort by function rank
      const idArray: number[] = Object.values(functionIds);
      const sortedSuggestions = sortByArrayAndField(
        suggestedDocumentsList.tasks,
        idArray,
        'executiveFunction',
        'name',
        functionIds.common,
      );
      return sortedSuggestions
        .filter((t) => {
          return template[list].includes(t.pageId);
        })
        .map((t) => {
          const executiveFunction = functions.find((f) => f.mainTemplateId === t.executiveFunction);
          const responsibleFunction = functions.find((f) => f.mainTemplateId === t.responsibleFunction);
          return {
            accessible: true,
            suggestion: true,
            mainIcon: 'suggestion',
            label: t.name,
            rightLabel: executiveFunction?.name ?? '',
            rightLabelClass: 'assignee',
            templateId: t.pageId,
            functionUuid: executiveFunction?.uuid ?? '',
            responsibleFunctionUuid: responsibleFunction?.uuid ?? '',
          };
        });
    }
  }
  return [];
}

export async function buildRiskAssessmentView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<RiskAssessmentView> {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const item = organization.riskAssessmentsById[uuid];
  assertEntityFound(item, 'riskAssessments', uuid);

  const entity = toRiskAssessmentViewModel(item);
  const substance = entity.relatedSubstance ? organization.substancesById[entity.relatedSubstance] : undefined;

  const dataItems = calculateDataItems(state);
  const dataItem = entity.relatedDataItem ? dataItems.find((x) => x.uuid === entity.relatedDataItem) : undefined;

  const relatedSubstanceHref = substance
    ? shortenPath(currentParent + '/riskAssessments/' + uuid + '/substances/' + substance?.uuid)
    : '';

  let helpContent = entity.helpContent ?? '';
  if (substance) {
    helpContent = defaultHelpForSubstancesRiskAssessment();
  }

  const href = currentParent + '/riskAssessments/' + uuid;

  // measures hentes direkte fra API; sammenslåing av determinedBy/responsibleBy
  // gjøres i backend-migreringen (dabih-api#4502), ikke her. (dabih-web#12750)
  const measures = entity.measures ?? '';
  const relatedEventUuids: string[] = entity.relatedEvents ?? [];
  const relatedTaskUuids: string[] = entity.relatedTasks ?? [];

  const currentUserHasWriteAccess = writeAccess(state);
  const pages = (entity.pages ?? []).map((x) => '' + x);
  const emergencyContent = pages.includes(pageIds.emergency + '');
  const currentUserCanEdit =
    currentUserHasWriteAccess && (!emergencyContent || commonData.currentUserHasEmergencyAccess);

  const relatedEvents: RiskAssessmentListItem[] = eventOccurrencesNotDeleted(state)
    .filter((item) => relatedEventUuids.includes(item.uuid))
    .map((item) => {
      const listItem = eventToListItem(
        item,
        functionsNotDeleted(state),
        employeesShortNamesAndI(state),
        getContactsAsUsers(state),
      );
      let secondaryLabel: string | undefined = undefined;
      if (item.doneDate) {
        secondaryLabel =
          t('content.donePrefix') +
          eventDoneText(item, employeesAndContactsOptions(state, item.employees), singleUserVersion(state), false);
      }
      let deleteButton = false;
      if (currentUserCanEdit) {
        deleteButton = true;
      }
      return {
        ...listItem,
        secondaryLabel,
        href: href + listItem.href,
        deleteButton,
        entityUuid: item.uuid,
        createdFromTemplate: item.createdFromTemplate,
      };
    });

  const relatedTasksUnsorted = tasksNotDeleted(state).filter((item) => relatedTaskUuids.includes(item.uuid));
  const relatedTasksSorted = sortByArrayAndField(
    relatedTasksUnsorted,
    functionUuidsByRank(state),
    'functionUuid',
    'name',
    commonFunctionUuid(state) ?? '',
  );
  const relatedTasks: RiskAssessmentListItem[] = relatedTasksSorted.map((item) => {
    const listItem = taskToListItem(
      item,
      functionsNotDeleted(state),
      employeesShortNamesAndI(state),
      getContactsAsUsers(state),
    );
    let deleteButton = false;
    if (currentUserCanEdit) {
      deleteButton = true;
    }
    return {
      ...listItem,
      href: href + listItem.href,
      deleteButton,
      entityUuid: item.uuid,
      createdFromTemplate: item.createdFromTemplate,
    };
  });

  const availableEvents: RiskAssessmentListItem[] = availableEventsAsListItems(state)
    .map((item) => ({
      ...item,
      href: href + item.href,
      entityUuid: item.clickData,
    }))
    .filter((item) => !relatedEventUuids.includes(item.entityUuid ?? ''));
  const availableTasks: RiskAssessmentListItem[] = tasksAsListItems(state)
    .map((item) => ({
      ...item,
      href: href + item.href,
      entityUuid: item.clickData,
    }))
    .filter((item) => !relatedTaskUuids.includes(item.entityUuid ?? ''));

  const probabilityDescription = entity.probabilityDescription ?? '';
  const causeCategories: string[] = entity.causeCategories ?? [];
  const measuresCardPublished = entity.measuresCardPublished;
  let causeCategoriesDisplay = '';
  const textArray: string[] = emergencyCauseCategories
    .filter((c) => {
      return causeCategories.includes(c.value);
    })
    .map((c) => {
      return c.text;
    });
  if (textArray.length) {
    causeCategoriesDisplay = joinWithAnd(textArray);
  }

  let defaultFunctionUuid = '';
  const defaultFunction = functionsNotDeleted(state).find((f) => f.mainTemplateId === functionIds.generalManager);
  if (defaultFunction) {
    defaultFunctionUuid = defaultFunction.uuid ?? '';
  }

  const suggestedEvents = getSuggestedMeasures(item.createdFromTemplate, 'suggestedEvents', functionsNotDeleted(state));
  const suggestedTasks = getSuggestedMeasures(item.createdFromTemplate, 'suggestedTasks', functionsNotDeleted(state));

  const functions = functionsNotDeleted(state);
  const suggestedTaskIds = suggestedTasks.map((t) => t.templateId);
  const suggestedEventIds = suggestedEvents.map((t) => t.templateId);

  const docsIndex = docsForLinkingById(state);

  const eventsFromSuggestions: TaskSummary[] = eventOccurrencesNotDeleted(state)
    .filter((e) => {
      return (
        e.createdFromTemplate !== 0 &&
        e.createdFromTemplate !== undefined &&
        suggestedEventIds.includes(e.createdFromTemplate)
      );
    })
    .map((e) => {
      const relatedRiskAssessments: string = joinWithAnd(
        riskAssessmentsNotDeleted(state)
          .filter((r) => r.name !== undefined && r.relatedEvents?.includes(e.uuid))
          .map((r) => r.name ?? ''),
      );
      return {
        uuid: e.uuid,
        templateId: 0,
        createdFromTemplate: e.createdFromTemplate ?? 0,
        name: e.name ?? '',
        time: dateTimeDescription(e.date ?? '', e.time ?? '', e.durationMinutes),
        procedure: convertInternalLinks(e.description, docsIndex),
        assignedTo: eventAssigneesText(
          e,
          functionsNotDeleted(state),
          employeesShortNamesAndMe(state),
          getContactsAsUsers(state),
        ),
        relatedRiskAssessments,
      };
    });

  const tasksFromSuggestions: TaskSummary[] = tasksNotDeleted(state)
    .filter((t) => {
      return (
        t.createdFromTemplate !== 0 &&
        t.createdFromTemplate !== undefined &&
        suggestedTaskIds.includes(t.createdFromTemplate)
      );
    })
    .map((t) => {
      const executiveFunction = functions.find((f) => f.uuid === t.functionUuid);
      const responsibleFunction = functions.find((f) => f.uuid === t.responsibleFunctionUuid);
      const relatedRiskAssessments: string = joinWithAnd(
        riskAssessmentsNotDeleted(state)
          .filter((r) => r.name !== undefined && r.relatedTasks?.includes(t.uuid))
          .map((r) => r.name ?? ''),
      );
      return {
        uuid: t.uuid,
        templateId: 0,
        createdFromTemplate: t.createdFromTemplate ?? 0,
        name: t.name ?? '',
        procedure: convertInternalLinks(t.procedures ?? '', docsIndex),
        assignedTo: executiveFunction?.name ?? '',
        responsibleFunction: responsibleFunction?.name ?? '',
        relatedRiskAssessments,
      };
    });

  const suggestedTaskSummaries: TaskSummary[] = [];
  [...suggestedEventIds, ...suggestedTaskIds].forEach((id) => {
    const suggestion = suggestedDocumentsList.tasks.find((t) => t.pageId === id);
    if (suggestion) {
      const executiveFunction = functions.find((f) => f.mainTemplateId === suggestion.executiveFunction);
      const responsibleFunction = functions.find((f) => f.mainTemplateId === suggestion.responsibleFunction);
      suggestedTaskSummaries.push({
        uuid: '',
        templateId: suggestion.pageId,
        createdFromTemplate: 0,
        name: suggestion.name,
        procedure: convertInternalLinks(suggestion.procedures, docsIndex),
        assignedTo: executiveFunction?.name ?? '',
        responsibleFunction: responsibleFunction?.name ?? '',
        relatedRiskAssessments: '',
      });
    }
  });

  let riskLevels = `<ul class="alert-list"><li>${t('riskAssessments.riskLevelDocumentIsMissing')}</li></ul>`;
  const riskLevelTemplateId = 470866;
  const riskLevelsGuideline = guidelinesNotDeleted(state).find((g) => g.mainTemplateId === riskLevelTemplateId);
  if (riskLevelsGuideline) {
    if (isEmptyHtmlContent(riskLevelsGuideline.content)) {
      riskLevels = `<p>${t('riskAssessments.riskLevelDocumentIsEmpty')}</p>`;
    } else {
      riskLevels = riskLevelsGuideline.content ?? '';
    }
    riskLevels += `<br><ul class="info-list"><li>${t('riskAssessments.riskLevelInfo')}</li></ul>`;
  }

  return {
    ...commonData,
    promoData: getPromoDataForEntity('riskAssessments', item, getFeatureStates(state)),
    docsForLinking: [],
    contentLastModified: entity.contentLastModified ?? '',
    contentLastModifiedBy: entity.contentLastModifiedBy ?? '',
    contentLastModifiedWasCreate: entity.contentLastModifiedWasCreate ?? false,
    hasTemplateDelete: entity.hasTemplateDelete ?? false,
    hasTemplateUpdate: entity.hasTemplateUpdate ?? false,
    templateDeclined: entity.templateDeclined ?? '',
    templateDeleted: entity.templateDeleted ?? '',
    templateDeletedMessage: entity.templateDeletedMessage ?? '',
    templateUpdated: entity.templateUpdated ?? '',
    currentUserHasAccess: true,
    currentUserHasWriteAccess,
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent,
    href,
    pdfHref:
      BASE_PATH +
      '/organizations/' +
      organization.organizationId +
      '/riskAssessments/' +
      uuid +
      '.pdf?key=' +
      key(state),
    createdFromTemplate: entity.createdFromTemplate,
    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'riskAssessments',
    name: entity.name ?? '',
    unchangedTemplate: entity.unchangedTemplate ?? false,
    revisions: await fetchRevisions(
      api,
      'riskAssessments',
      uuid,
      organization.organizationId,
      entity.templateDeleted !== undefined && entity.templateDeleted !== '',
      undefined,
      entity.contentLastModified,
    ),
    attachments: toAttachmentItems(api, 'riskAssessments', entity.uuid, entity.attachments),
    probability: entity.probability ?? 0,
    consequence: entity.consequence ?? 0,
    calculatedRisk: (entity.consequence ?? 0) * (entity.probability ?? 0),
    probabilityDisplayText: getProbabilityDisplayText(entity.probability ?? 0),
    consequenceDisplayText: getConsequenceDisplayText(entity.consequence ?? 0),
    calculatedRiskDisplayText: getCalculatedRiskDisplayText((entity.consequence ?? 0) * (entity.probability ?? 0)),
    measures,
    substanceRelated: substance !== undefined,
    relatedSubstance: substance?.uuid ?? '',
    relatedSubstanceName: substance?.name ?? '',
    relatedSubstanceHref: relatedSubstanceHref ?? '',
    dataItemRelated: dataItem !== undefined,
    relatedDataItem: dataItem?.uuid ?? '',
    relatedDataItemName: dataItem?.name ?? '',
    relatedDataItemHref:
      dataItem !== undefined
        ? '/account/' + organization.organizationId + '/20?tab=map&c=' + dataItem?.storageUnit
        : '',
    informationSecurityDescription: informationSecurityDescription(entity),
    riskDescription: entity.riskDescription ?? '',
    description: entity.description ?? '',
    assessmentDate: entity.assessmentDate ?? '',
    availablePages: pagesAsSelectTagOptions(state).map((p) => ({
      ...p,
      disabled: p.value === pageIds.riskAssessments + '',
    })),
    availableCategories: pagesAsSelectTagOptions(state, true).map((p) => ({
      ...p,
      disabled: p.value === pageIds.riskAssessments + '',
    })),
    pages,
    violatesInformationSecurityRequirements: entity.violatesInformationSecurityRequirements === true,
    violatesConfidentialityRequirement: entity.violatesConfidentialityRequirement === true,
    violatesIntegrityRequirement: entity.violatesIntegrityRequirement === true,
    violatesNonRepudiationRequirement: entity.violatesNonRepudiationRequirement === true,
    violatesAvailabilityRequirement: entity.violatesAvailabilityRequirement === true,
    today: LocalDate.fromString(state.today),
    employeesOptions: getEmployeesOptions(employeesNotDeleted(state)),
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'riskAssessments', uuid, organization.organizationId);
      return command.draft as RiskAssessmentViewEditItem;
    },
    previousAssessments: getPreviousAssessmentsAsListItems(riskAssessmentsNotDeleted(state), item, currentParent),
    isNewest: getIsNewest(riskAssessmentsNotDeleted(state), item),
    userEmails: getUsersForAccess(state)
      .map((e) => e.email ?? '')
      .filter((x) => x !== ''),
    probabilityDescription,
    causeCategories,
    causeCategoriesDisplay,
    availableEvents,
    availableTasks,
    relatedEvents,
    relatedTasks,
    suggestedEvents,
    eventsFromSuggestions,
    suggestedTasks,
    suggestedTaskSummaries,
    tasksFromSuggestions,
    defaultFunctionUuid,
    emergencyContent,
    measuresCardPublished,
    riskLevels,
    relatedTasksForPdf: relatedTasksUnsorted,
    functionNames: functionsByRank(state).map((f) => ({ uuid: f.uuid, name: f.name ?? '' })),
    partnersAndPeopleWithContactInfo: getPartnersAndPeopleWithContactInfo(state),
  };
}
